import React, { useRef, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Icon } from "../../components/Component";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import dayjs from "dayjs";

interface WorkBoqPdfGeneratorProps {
  workBoqData: any;
  orgLogo?: string;
  orgName?: string;
  getPdfUrl?: (blob: Blob) => void;
}

const WorkBoqPdfGenerator: React.FC<WorkBoqPdfGeneratorProps> = ({
  workBoqData,
  orgLogo,
  orgName = "Your Organization",
  getPdfUrl,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (download: boolean = false) => {
    if (!componentRef.current) return;

    try {
      setIsGenerating(true);
      
      // Get the original element and temporarily make it visible
      const element = componentRef.current;
      
      // Store original styles
      const originalDisplay = element.style.display;
      const originalPosition = element.style.position;
      const originalLeft = element.style.left;
      const originalTop = element.style.top;
      const originalZIndex = element.style.zIndex;
      
      // Make element visible but off-screen for rendering
      element.style.display = "block";
      element.style.position = "fixed";
      element.style.left = "-9999px";
      element.style.top = "0";
      element.style.zIndex = "-1000";

      // Wait a moment for React to render the content
      await new Promise(resolve => setTimeout(resolve, 100));

      // Use html2canvas to capture the component
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: element.scrollWidth,
        height: element.scrollHeight,
        logging: false,
        onclone: (clonedDoc) => {
          // Add additional CSS rules to the cloned document
          const style = clonedDoc.createElement('style');
          style.innerHTML = `
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            div[data-work-item] {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              display: block !important;
            }
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid !important;
              break-after: avoid !important;
              orphans: 3 !important;
            }
          `;
          clonedDoc.head.appendChild(style);
        }
      });

      // Restore original styles
      element.style.display = originalDisplay;
      element.style.position = originalPosition;
      element.style.left = originalLeft;
      element.style.top = originalTop;
      element.style.zIndex = originalZIndex;

      // Create PDF
      const pdf = new jsPDF("p", "mm", "a4");
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate dimensions
      const marginTop = 10; // mm
      const marginBottom = 15; // mm for page numbers
      const marginSide = 5; // mm
      const contentWidth = pdfWidth - (marginSide * 2);
      const contentHeight = pdfHeight - marginTop - marginBottom;
      
      // Convert to pixels at 96 DPI
      const contentWidthPx = (contentWidth / 25.4) * 96;
      const contentHeightPx = (contentHeight / 25.4) * 96;
      
      // Calculate scale to fit content width
      const scale = contentWidthPx / canvas.width;
      const scaledCanvasHeight = canvas.height * scale;
      
      // Calculate total pages needed
      const totalPages = Math.ceil(scaledCanvasHeight / contentHeightPx);
      
      let currentPage = 1;
      
      // Process each page
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
          currentPage++;
        }
        
        // Calculate source coordinates for this page
        const sourceY = (page * contentHeightPx) / scale;
        const remainingHeight = canvas.height - sourceY;
        const sourceHeight = Math.min(contentHeightPx / scale, remainingHeight);
        
        if (sourceHeight > 0) {
          // Create temporary canvas for this page
          const pageCanvas = document.createElement('canvas');
          const pageCtx = pageCanvas.getContext('2d');
          
          if (pageCtx) {
            // Set canvas size
            pageCanvas.width = canvas.width;
            pageCanvas.height = sourceHeight;
            
            // Fill with white background
            pageCtx.fillStyle = '#ffffff';
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            
            // Draw the content for this page
            pageCtx.drawImage(
              canvas,
              0, sourceY, // source coordinates
              canvas.width, sourceHeight, // source dimensions
              0, 0, // destination coordinates
              canvas.width, sourceHeight // destination dimensions
            );
            
            // Convert to image data
            const pageImgData = pageCanvas.toDataURL("image/png", 0.9);
            
            // Calculate PDF dimensions
            const pdfContentHeight = sourceHeight * scale * 25.4 / 96; // Convert to mm
            
            // Add image to PDF
            pdf.addImage(
              pageImgData,
              "PNG",
              marginSide, // x position
              marginTop, // y position
              contentWidth, // width
              pdfContentHeight, // height
              undefined,
              'FAST'
            );
          }
        }
        
        // Add page number at the bottom
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text(
          `Page ${currentPage} of ${totalPages}`, 
          pdfWidth - 30, 
          pdfHeight - 5 // Fixed position at bottom
        );
      }

      // Generate blob
      const generatedPdfBlob = pdf.output("blob");
      setPdfBlob(generatedPdfBlob);
      
      // Create URL for preview
      const url = URL.createObjectURL(generatedPdfBlob);
      setPdfUrl(url);

      // Generate blob for email attachment if callback provided
      if (getPdfUrl) {
        getPdfUrl(generatedPdfBlob);
      }

      if (download) {
        // Download the PDF
        pdf.save(`Work_BOQ_${workBoqData.id}_${dayjs().format("DDMMYYYY")}.pdf`);
      }

      setIsGenerating(false);
      return generatedPdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsGenerating(false);
      throw error;
    }
  };

  const handlePreview = async () => {
    try {
      await generatePDF(false);
      setShowPreview(true);
    } catch (error) {
      console.error("Error generating PDF preview:", error);
    }
  };

  const handleDownload = async () => {
    try {
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Work_BOQ_${workBoqData.id}_${dayjs().format("DDMMYYYY")}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        await generatePDF(true);
      }
      setShowPreview(false);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
    setPdfBlob(null);
  };

  const formatDecimal = (value: number) => {
    return Number(value).toFixed(2);
  };

  return (
    <>
      {/* PDF Generation Button */}
      <Button 
        className="btn btn-white btn-outline-light" 
        onClick={handlePreview}
        disabled={isGenerating}
      >
        {isGenerating ? (
          <>
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            Generating...
          </>
        ) : (
          <>
            <Icon name="download" />
            <span>Export PDF</span>
          </>
        )}
      </Button>

      {/* PDF Preview Modal */}
      <Modal 
        isOpen={showPreview} 
        toggle={closePreview} 
        size="xl"
        className="modal-dialog-centered"
        backdrop="static"
      >
        <ModalHeader toggle={closePreview}>
          <div className="d-flex align-items-center">
            <Icon name="file-docs" className="me-2" />
            Work BOQ PDF Preview - {workBoqData.id}
          </div>
        </ModalHeader>
        <ModalBody style={{ padding: 0, maxHeight: "70vh", overflow: "auto" }}>
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              width="100%"
              height="600px"
              style={{ border: "none" }}
              title="PDF Preview"
            />
          )}
        </ModalBody>
        <ModalFooter>
          <div className="d-flex justify-content-between w-100">
            <div className="text-muted small">
              <Icon name="info" className="me-1" />
              Preview generated on {dayjs().format("DD-MMM-YYYY HH:mm")}
            </div>
            <div>
              <Button color="primary" onClick={handleDownload}>
                <Icon name="download" className="me-1" />
                Download PDF
              </Button>
              <Button color="secondary" onClick={closePreview} className="ms-2">
                Close
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Hidden PDF Template - Compact Design */}
      <div
        ref={componentRef}
        data-pdf-template="true"
        style={{
          display: "none",
          width: "210mm",
          minHeight: "297mm",
          padding: "15px",
          backgroundColor: "white",
          fontFamily: "'Arial', sans-serif",
          fontSize: "12px",
          lineHeight: "1.4",
          color: "#333",
        }}
      >
        {/* Compact Header */}
        <div
          style={{
            borderBottom: "2px solid #666",
            paddingBottom: "12px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {orgLogo && (
              <img
                src={orgLogo}
                alt={orgName}
                style={{ 
                  height: "40px", 
                  marginRight: "12px"
                }}
              />
            )}
            <div>
              <h1 style={{ 
                color: "#333", 
                margin: "0", 
                fontSize: "18px",
                fontWeight: "bold" 
              }}>
                {orgName}
              </h1>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <h2
              style={{
                color: "#666",
                margin: "0 0 4px 0",
                fontSize: "16px",
                fontWeight: "bold"
              }}
            >
              WORK BOQ
            </h2>
            <p style={{ margin: "0", fontSize: "11px", color: "#666" }}>
              ID: {workBoqData.id} | {dayjs().format("DD-MMM-YYYY")}
            </p>
          </div>
        </div>

        {/* Compact Information Grid */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "12px",
          marginBottom: "20px",
          fontSize: "11px"
        }}>
          <div>
            <strong>BOQ Title:</strong><br />
            {workBoqData.boqTitle || "N/A"}
          </div>
          <div>
            <strong>Site:</strong><br />
            {workBoqData.siteName || "N/A"}
          </div>
          <div>
            <strong>Project:</strong><br />
            {workBoqData.projectName || "N/A"}
          </div>
          <div>
            <strong>Contractor:</strong><br />
            {workBoqData.contractorName || "N/A"}
          </div>
          <div>
            <strong>Work Level:</strong><br />
            {workBoqData.workLevel || "N/A"}
          </div>
          <div>
            <strong>Status:</strong><br />
            {workBoqData.status || "N/A"}
          </div>
        </div>

        {/* Description */}
        {workBoqData.boqDescription && (
          <div style={{ marginBottom: "20px" }}>
            <strong style={{ fontSize: "11px" }}>Description:</strong>
            <div
              style={{
                backgroundColor: "#f9f9f9",
                border: "1px solid #ddd",
                padding: "8px",
                fontSize: "11px",
                marginTop: "4px"
              }}
            >
              {workBoqData.boqDescription}
            </div>
          </div>
        )}

        {/* Work Order Lines */}
        <div style={{ 
          marginBottom: "20px",
          pageBreakInside: "avoid"
        }}>
          <h3 style={{
            margin: "0 0 12px 0",
            fontSize: "14px",
            color: "#333",
            borderBottom: "1px solid #ddd",
            paddingBottom: "4px",
            pageBreakAfter: "avoid"
          }}>
            Work Order Lines ({workBoqData.workOrderLines?.length || 0} items)
          </h3>

          {!workBoqData.workOrderLines || workBoqData.workOrderLines.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                backgroundColor: "#f9f9f9",
                border: "1px dashed #ccc",
                color: "#666",
                fontSize: "11px"
              }}
            >
              No work order lines found
            </div>
          ) : (
            <div style={{ 
              orphans: 3, 
              widows: 3 
            }}>
              {workBoqData.workOrderLines.map((line: any, index: number) => (
                <div
                  key={index}
                  data-work-item="true"
                  style={{
                    border: "1px solid #ddd",
                    marginBottom: "8px",
                    padding: "10px",
                    fontSize: "11px",
                    pageBreakInside: "avoid",
                    breakInside: "avoid",
                    display: "block",
                    minHeight: "120px" // Ensure minimum space requirement
                  }}
                >
                  {/* Task Header */}
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "8px",
                    pageBreakAfter: "avoid"
                  }}>
                    <div>
                      <strong style={{ fontSize: "12px" }}>
                        {index + 1}. {line.taskTitle || "Untitled Task"}
                      </strong>
                      {line.workSubCategory && (
                        <div style={{ color: "#666", fontSize: "10px" }}>
                          {line.workSubCategory}
                        </div>
                      )}
                    </div>
                    <span style={{
                      backgroundColor: "#f0f0f0",
                      color: "#666",
                      padding: "2px 6px",
                      fontSize: "10px",
                      border: "1px solid #ccc"
                    }}>
                      {line.workCategory || "General"}
                    </span>
                  </div>

                  {/* Compact Details */}
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: workBoqData.workLevel !== "Project" ? "1fr 1fr 1fr" : "1fr 1fr",
                    gap: "8px",
                    marginBottom: "8px",
                    fontSize: "10px"
                  }}>
                    <div>
                      <strong>Qty:</strong> {formatDecimal(line.quantity)} {line.uom || ""}
                    </div>
                    
                    {workBoqData.workLevel !== "Project" && (
                      <div>
                        <strong>Location:</strong> {
                          [line.towerVilla, line.floorName, line.flatName]
                            .filter(Boolean)
                            .join(" / ") || "-"
                        }
                      </div>
                    )}
                    
                    <div>
                      <strong>Docs:</strong> {
                        line.uploadedDocs?.length > 0 ? 
                          `${line.uploadedDocs.length} file(s)` : 
                          "None"
                      }
                    </div>
                  </div>

                  {/* Description */}
                  {line.taskDescription && (
                    <div style={{ marginBottom: "8px", fontSize: "10px" }}>
                      <strong>Description:</strong> {line.taskDescription}
                    </div>
                  )}

                  {/* Materials */}
                  <div style={{ fontSize: "10px" }}>
                    <strong>Materials ({line.allocatedMaterials?.length || 0}):</strong>
                    {line.allocatedMaterials && line.allocatedMaterials.length > 0 ? (
                      <div style={{ marginTop: "4px" }}>
                        {line.allocatedMaterials.map((material: any, matIndex: number) => (
                          <span
                            key={matIndex}
                            style={{
                              display: "inline-block",
                              backgroundColor: "#f0f0f0",
                              color: "#666",
                              padding: "1px 4px",
                              margin: "2px 4px 2px 0",
                              fontSize: "9px",
                              border: "1px solid #ccc"
                            }}
                          >
                            {material.materialName || material.name || material}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: "#999", fontStyle: "italic" }}> None</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Compact Summary */}
        {workBoqData.workOrderLines?.length > 0 && (
          <div style={{ 
            marginBottom: "20px",
            pageBreakInside: "avoid"
          }}>
            <h3 style={{
              margin: "0 0 8px 0",
              fontSize: "14px",
              color: "#333",
              borderBottom: "1px solid #ddd",
              paddingBottom: "4px",
              pageBreakAfter: "avoid"
            }}>
              Summary
            </h3>

            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "1fr 1fr 1fr", 
              gap: "12px",
              fontSize: "11px"
            }}>
              <div style={{
                textAlign: "center",
                padding: "8px",
                border: "1px solid #ddd"
              }}>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}>
                  {workBoqData.workOrderLines.length}
                </div>
                <div>Total Lines</div>
              </div>

              <div style={{
                textAlign: "center",
                padding: "8px",
                border: "1px solid #ddd"
              }}>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}>
                  {formatDecimal(
                    workBoqData.workOrderLines.reduce(
                      (total: number, line: any) =>
                        total + (Number(line.quantity) || 0),
                      0
                    )
                  )}
                </div>
                <div>Total Quantity</div>
              </div>

              <div style={{
                textAlign: "center",
                padding: "8px",
                border: "1px solid #ddd"
              }}>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}>
                  {
                    new Set(
                      workBoqData.workOrderLines.map(
                        (line: any) => line.workCategory
                      )
                    ).size
                  }
                </div>
                <div>Categories</div>
              </div>
            </div>
          </div>
        )}

        {/* Compact Footer */}
        <div
          style={{
            borderTop: "1px solid #ddd",
            paddingTop: "8px",
            textAlign: "center",
            fontSize: "9px",
            color: "#666",
            marginTop: "20px",
          }}
        >
          Generated on {dayjs().format("DD-MMM-YYYY HH:mm")} | © {dayjs().format("YYYY")} {orgName}
        </div>
      </div>
    </>
  );
};

export default WorkBoqPdfGenerator;